import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { testimonials } from '@/mocks/testimonials';
import { faqItems } from '@/mocks/faq';

const aboutJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "好色精灵探访记",
      "url": "https://haosejingling.com/",
      "description": "好色精灵探访记是一部充满奇幻色彩与创意的互动体验故事，致力于用色彩的力量传递美好与希望。",
      "foundingDate": "2026",
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "availableLanguage": ["Chinese"]
      }
    },
    {
      "@type": "WebPage",
      "name": "关于好色精灵探访记",
      "url": "https://haosejingling.com/about",
      "description": "了解好色精灵探访记的品牌故事、读者口碑和联系方式",
      "inLanguage": "zh-CN",
      "isPartOf": { "@id": "https://haosejingling.com/#website" }
    }
  ]
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('website_alt') as string || '').trim();

    if (honeypotVal) {
      setFormStatus('success');
      setFormMessage('提交成功！感谢你的留言，我们会尽快回复。');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qrc3knlsngrm4lm4l0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(responseText); } catch {}

      if (response.ok && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功！感谢你的留言，我们会尽快回复。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.toLowerCase().includes('spam') || serverMsg.includes('spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查网络连接后重试。');
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className="w-3 h-3 flex items-center justify-center">
        <i className={`text-xs ${i < rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
      </span>
    ));
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
      />
      <ScrollToTop />
      <Navbar transparent={false} />
      <main className="pt-16">
        <div className="px-4 md:px-6 py-8 md:py-12">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />

            <section className="mb-16">
              <div className="text-center mb-10">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
                  关于好色精灵探访记
                </h1>
                <p className="text-sm md:text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
                  好色精灵探访记是一部奇幻色彩冒险互动体验故事，自诞生以来便以独特的色彩世界观和动人的精灵故事，
                  吸引了无数热爱奇幻与色彩的读者。我们致力于用色彩的力量传递美好与希望。
                </p>
              </div>

              <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-10">
                <img
                  src="https://readdy.ai/api/search-image?query=Magical%20fantasy%20world%20with%20floating%20islands%20and%20rainbow%20waterfalls%2C%20ethereal%20elves%20flying%20in%20a%20colorful%20sunset%20sky%2C%20warm%20golden%20light%2C%20dreamy%20atmosphere%2C%20enchanting%20fantasy%20illustration%20style%2C%20rich%20vibrant%20color%20palette%2C%20whimsical%20and%20magical%20mood%2C%20painterly%20texture%2C%20cinematic%20wide%20composition&width=1200&height=500&seq=about-brand-story&orientation=landscape"
                  alt="好色精灵探访记品牌故事"
                  title="好色精灵探访记品牌故事"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div className="bg-background-100 rounded-lg p-6">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary-100 flex items-center justify-center">
                    <i className="ri-eye-line text-xl text-primary-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的愿景</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    用色彩的力量点亮每一个人的内心世界，让想象力自由飞翔，让每一个人都能在生活中找到属于自己的色彩魔法。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                    <i className="ri-compasses-2-line text-xl text-accent-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">创作理念</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    每一段故事、每一位精灵、每一种色彩，都经过精心设计与打磨。我们相信，最美的故事能够跨越年龄与地域，直抵人心。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-6">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-group-line text-xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">我们的团队</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    由一群热爱奇幻文学的创作者、插画师和故事设计师组成，我们用心打造每一个篇章，只为带给你最美的色彩冒险体验。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  读者口碑
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  听听我们的读者怎么说
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {testimonials.map((test) => (
                  <div key={test.id} className="bg-background-100 rounded-lg p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-200">
                        <i className={`${test.avatar} text-sm text-foreground-500`}></i>
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-900">{test.name}</p>
                        <p className="text-xs text-foreground-400">{test.date}</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 mb-3">{renderStars(test.rating)}</div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{test.content}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="mb-12">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  联系我们
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
                  有任何想对我们说的？无论是合作意向、创意投稿还是简单打个招呼，我们都期待听到你的声音！
                </p>
              </div>
              <div className="max-w-xl mx-auto bg-background-100 rounded-lg p-6 md:p-8">
                <form data-readdy-form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1">
                        你的名字
                      </label>
                      <input
                        type="text"
                        id="contact_name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入你的名字"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="contact_email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1">
                        留言内容
                      </label>
                      <textarea
                        id="contact_message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入你想说的话（最多500字）"
                      ></textarea>
                    </div>
                    <div className="anti-spam-field">
                      <label htmlFor="website_alt">Website</label>
                      <input
                        type="text"
                        id="website_alt"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      提交留言
                    </button>
                  </div>
                  {formStatus === 'success' && (
                    <p className="mt-4 text-sm text-green-600 bg-green-50 p-3 rounded-lg">{formMessage}</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="mt-4 text-sm text-red-600 bg-red-50 p-3 rounded-lg">{formMessage}</p>
                  )}
                </form>
              </div>
            </section>

            <section className="border-t border-background-200 pt-12">
              <div className="text-center mb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                  常见问题
                </h2>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqItems.map((item, index) => (
                  <div key={index} className="bg-background-100 rounded-lg p-5">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2">{item.question}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}